import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  PlayCircleFilled,
  FilePdfOutlined,
  GlobalOutlined,
  EditOutlined,
} from '@ant-design/icons'
import { Button, Input, message } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import {
  LightboxOverlay,
  LightboxCloseBtn,
  LightboxCounter,
  LightboxNavBtn,
  LightboxContent,
  LightboxThumbs,
  LightboxThumb,
  PhotoViewerLayout,
  PhotoViewerMediaPane,
  PhotoViewerSidebar,
} from '../../NewsFeedStyled'
import { apiUrl } from '../../../../config'
import YoutubeVideoPlayer from '../YoutubeVideoPlayer/YoutubeVideoPlayer'
import PDFViewer from '../../../../components/PDFViewer/PDFViewer'
import CommentSection from '../CommentSection/CommentSection'
import { NewsFeedRequest } from '../../../../requests/NewsFeedRequest'
import utils from '../../../../utils'

/** Resolve media URL */
const resolveMediaUrl = m => {
  if (!m) return null
  const url = m.fileUrl || m.file_url
  if (url) {
    if (
      url.startsWith('http') ||
      url.startsWith('blob:') ||
      url.startsWith('data:')
    ) {
      return url
    }
    if (url.startsWith('/')) return `${apiUrl}${url}`
    if (/^[0-9a-fA-F]{24}$/.test(url)) {
      return `${apiUrl}/api/v1/images/${url}`
    }
    return `${apiUrl}/${url}`
  }
  const id = m.fileId || m.file_id
  if (id) {
    return `${apiUrl}/api/v1/images/${id}`
  }
  return null
}

const formatDisplayName = name => {
  if (!name) return ''
  return name
    .toLowerCase()
    .split(' ')
    .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ')
}

/**
 * LightboxViewer - Xem ảnh fullscreen
 * Khi có `post`: layout kiểu Facebook (ảnh + sidebar comment/sửa caption từng ảnh)
 */
const LightboxViewer = ({
  media = [],
  initialIndex = 0,
  onClose,
  post = null,
  authenticationStore,
  newsFeedStore,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [touchStart, setTouchStart] = useState(null)
  const [isEditingCaption, setIsEditingCaption] = useState(false)
  const [captionDraft, setCaptionDraft] = useState('')
  const [isSavingCaption, setIsSavingCaption] = useState(false)
  const [localMedia, setLocalMedia] = useState(media)
  const overlayRef = useRef(null)

  useEffect(() => {
    setLocalMedia(media)
  }, [media])

  useEffect(() => {
    setIsEditingCaption(false)
  }, [currentIndex])

  const total = localMedia.length
  const currentMedia = localMedia[currentIndex]
  const currentMediaId = currentMedia?.id || currentMedia?.mediaId
  const isOwner =
    !!post &&
    !!authenticationStore?.currentUser?.username &&
    authenticationStore.currentUser.username ===
      (post.user_code || post.userCode)

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : total - 1))
  }, [total])

  const goNext = useCallback(() => {
    setCurrentIndex(prev => (prev < total - 1 ? prev + 1 : 0))
  }, [total])

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') onClose()

      const isVideo =
        currentMedia &&
        (currentMedia.mediaType === 'VIDEO' ||
          currentMedia.media_type === 'VIDEO')
      if (isVideo && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        return
      }

      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [currentIndex, currentMedia, goPrev, goNext, onClose])

  const handleTouchStart = useCallback(e => {
    setTouchStart(e.touches[0].clientX)
  }, [])

  const handleTouchEnd = useCallback(
    e => {
      if (touchStart === null) return
      const touchEnd = e.changedTouches[0].clientX
      const diff = touchStart - touchEnd
      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext()
        else goPrev()
      }
      setTouchStart(null)
    },
    [touchStart, goNext, goPrev]
  )

  const handleOverlayClick = useCallback(
    e => {
      if (e.target === overlayRef.current) {
        onClose()
      }
    },
    [onClose]
  )

  const startEditCaption = useCallback(() => {
    setCaptionDraft(currentMedia?.caption || '')
    setIsEditingCaption(true)
  }, [currentMedia])

  const saveCaption = useCallback(async () => {
    if (!post?.id || !currentMediaId) return
    try {
      setIsSavingCaption(true)
      const res = await NewsFeedRequest.updateMediaCaption(
        post.id,
        currentMediaId,
        captionDraft
      )
      const newCaption = res?.data?.caption ?? captionDraft.trim()
      setLocalMedia(prev =>
        prev.map((m, idx) =>
          idx === currentIndex ? { ...m, caption: newCaption } : m
        )
      )
      // Đồng bộ caption vào post trong store nếu có
      if (newsFeedStore?.feeds && post.media) {
        Object.keys(newsFeedStore.feeds).forEach(key => {
          const feedPost = newsFeedStore.feeds[key].posts.find(
            p => p.id === post.id
          )
          if (feedPost?.media) {
            feedPost.media = feedPost.media.map(m =>
              m.id === currentMediaId ? { ...m, caption: newCaption } : m
            )
            newsFeedStore.feeds[key].posts = [...newsFeedStore.feeds[key].posts]
          }
        })
      }
      setIsEditingCaption(false)
      message.success('Đã cập nhật caption')
    } catch (err) {
      console.error(err)
      message.error(err?.vi || 'Không thể cập nhật caption')
    } finally {
      setIsSavingCaption(false)
    }
  }, [
    post,
    currentMediaId,
    captionDraft,
    currentIndex,
    newsFeedStore,
  ])

  if (!currentMedia) return null

  const mediaType = currentMedia.mediaType || currentMedia.media_type
  const withSidebar = !!post?.id
  const avatarUrl = post?.avatar_url || post?.avatarUrl
  const displayName = formatDisplayName(
    post?.display_name || post?.displayName || post?.user_code
  )
  const createdAt = post?.created_at || post?.createdAt

  const renderMediaContent = () => (
    <>
      {mediaType === 'IMAGE' && (
        <img src={resolveMediaUrl(currentMedia)} alt="" key={currentIndex} />
      )}
      {mediaType === 'VIDEO' && (
        <YoutubeVideoPlayer
          src={resolveMediaUrl(currentMedia)}
          key={currentIndex}
        />
      )}
      {(mediaType === 'PDF' || mediaType === 'DOCUMENT') && (
        <div
          style={{
            width: withSidebar ? '90%' : '80vw',
            height: withSidebar ? '80%' : '80vh',
            background: '#fff',
            borderRadius: 12,
            overflow: 'hidden',
          }}>
          <PDFViewer
            fileId={
              currentMedia.fileId || currentMedia.file_id || currentMedia.id
            }
            fileType={utils.getExtensionFile(
              currentMedia.fileName || currentMedia.file_name
            )}
            fileName={currentMedia.fileName || currentMedia.file_name}
            key={currentIndex}
          />
        </div>
      )}
    </>
  )

  const renderThumbs = () =>
    total > 1 && (
      <LightboxThumbs className={withSidebar ? 'in-pane' : ''}>
        {localMedia.map((m, idx) => (
          <LightboxThumb
            key={m.id || idx}
            className={idx === currentIndex ? 'active' : ''}
            onClick={() => setCurrentIndex(idx)}>
            {(m.mediaType === 'IMAGE' || m.media_type === 'IMAGE') && (
              <img src={resolveMediaUrl(m)} alt="" />
            )}
            {(m.mediaType === 'VIDEO' || m.media_type === 'VIDEO') && (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#333',
                }}>
                <PlayCircleFilled style={{ color: '#fff', fontSize: 24 }} />
              </div>
            )}
            {(m.mediaType === 'PDF' ||
              m.media_type === 'PDF' ||
              m.mediaType === 'DOCUMENT' ||
              m.media_type === 'DOCUMENT') && (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#eee',
                }}>
                <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />
              </div>
            )}
          </LightboxThumb>
        ))}
      </LightboxThumbs>
    )

  if (!withSidebar) {
    return (
      <LightboxOverlay
        ref={overlayRef}
        onClick={handleOverlayClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}>
        <LightboxCloseBtn onClick={onClose}>
          <CloseOutlined />
        </LightboxCloseBtn>
        <LightboxCounter>
          {currentIndex + 1} / {total}
        </LightboxCounter>
        {total > 1 && (
          <LightboxNavBtn className="prev" onClick={goPrev}>
            <LeftOutlined />
          </LightboxNavBtn>
        )}
        <LightboxContent>{renderMediaContent()}</LightboxContent>
        {(currentMedia.caption || '').trim() && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              bottom: total > 1 ? 84 : 24,
              transform: 'translateX(-50%)',
              maxWidth: '80%',
              background: 'rgba(0,0,0,0.65)',
              color: '#fff',
              padding: '8px 14px',
              borderRadius: 8,
              fontSize: 14,
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              zIndex: 10002,
            }}>
            {currentMedia.caption}
          </div>
        )}
        {total > 1 && (
          <LightboxNavBtn className="next" onClick={goNext}>
            <RightOutlined />
          </LightboxNavBtn>
        )}
        {renderThumbs()}
      </LightboxOverlay>
    )
  }

  // Facebook-style photo viewer with sidebar
  return (
    <LightboxOverlay
      ref={overlayRef}
      className="with-sidebar"
      onClick={handleOverlayClick}>
      <LightboxCloseBtn onClick={onClose}>
        <CloseOutlined />
      </LightboxCloseBtn>

      <PhotoViewerLayout>
        <PhotoViewerMediaPane
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}>
          <LightboxCounter className="in-pane">
            {currentIndex + 1} / {total}
          </LightboxCounter>
          {total > 1 && (
            <LightboxNavBtn className="prev in-pane" onClick={goPrev}>
              <LeftOutlined />
            </LightboxNavBtn>
          )}
          <LightboxContent className="in-pane">
            {renderMediaContent()}
          </LightboxContent>
          {total > 1 && (
            <LightboxNavBtn className="next in-pane" onClick={goNext}>
              <RightOutlined />
            </LightboxNavBtn>
          )}
          {renderThumbs()}
        </PhotoViewerMediaPane>

        <PhotoViewerSidebar>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #3a3b3c',
              fontSize: 13,
              color: '#b0b3b8',
            }}>
            Ảnh này nằm trong một bài viết
          </div>

          <div
            style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              borderBottom: '1px solid #3a3b3c',
            }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#3a3b3c',
                flexShrink: 0,
              }}>
              {avatarUrl ? (
                <img
                  src={
                    avatarUrl.startsWith('http') || avatarUrl.startsWith('/')
                      ? avatarUrl.startsWith('http')
                        ? avatarUrl
                        : `${apiUrl}${avatarUrl}`
                      : `${apiUrl}/api/v1/images/${avatarUrl}`
                  }
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : null}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#e4e6eb' }}>
                {displayName}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#b0b3b8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                {createdAt ? moment(createdAt).fromNow() : ''}
                <GlobalOutlined style={{ fontSize: 11 }} />
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #3a3b3c',
            }}>
            {isEditingCaption ? (
              <div>
                <Input.TextArea
                  value={captionDraft}
                  onChange={e =>
                    setCaptionDraft(e.target.value.slice(0, 500))
                  }
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  maxLength={500}
                  placeholder="Thêm caption cho ảnh..."
                />
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    gap: 8,
                    justifyContent: 'flex-end',
                  }}>
                  <Button
                    size="small"
                    onClick={() => setIsEditingCaption(false)}
                    disabled={isSavingCaption}>
                    Hủy
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    loading={isSavingCaption}
                    onClick={saveCaption}>
                    Lưu
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                {(currentMedia.caption || '').trim() ? (
                  <div
                    style={{
                      fontSize: 14,
                      color: '#e4e6eb',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      marginBottom: isOwner ? 8 : 0,
                    }}>
                    {currentMedia.caption}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: 13,
                      color: '#b0b3b8',
                      fontStyle: 'italic',
                      marginBottom: isOwner ? 8 : 0,
                    }}>
                    Chưa có caption
                  </div>
                )}
                {isOwner && currentMediaId && (
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={startEditCaption}
                    style={{ fontWeight: 600 }}>
                    Chỉnh sửa
                  </Button>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '8px 12px 16px',
              minHeight: 0,
            }}>
            {currentMediaId ? (
              <CommentSection
                postId={post.id}
                mediaId={currentMediaId}
                compact
                dark
              />
            ) : (
              <div style={{ color: '#b0b3b8', fontSize: 13, padding: 8 }}>
                Không thể tải bình luận cho ảnh này.
              </div>
            )}
          </div>
        </PhotoViewerSidebar>
      </PhotoViewerLayout>
    </LightboxOverlay>
  )
}

export default inject(
  'authenticationStore',
  'newsFeedStore'
)(observer(LightboxViewer))
